// LicenseeGenie mockup pages
import FinancialForecasting from "../pages/financialforecasting";
import LicensedProductCatalog from "../pages/licensedproductcatalog";
import ComplianceMonitoring from "../pages/compliancemonitoring";
import ContractComparison from "../pages/contractcomparison";
import AuditTrail from "../pages/audittrail";
/**
 * Navigation Configuration Module - Application Routing and Page Definitions
 * 
 * Centralized routing configuration for the entire ContactGenie application
 * Defines all available routes, page components, and access control rules
 * 
 * Features:
 * - Complete route mapping with path-to-component binding
 * - Menu visibility control (isMenu property)
 * - Authentication state enforcement (isPrivate property)
 * - Client-specific routing (generic vs contract client types)
 * - Dynamic navigation array modification based on client type
 * 
 * Route Properties:
 * - path: URL path (can include parameters like :documentid, :type)
 * - name: Component name for identification
 * - element: React component to render
 * - isMenu: Boolean - show/hide in navigation menu (true = visible)
 * - isPrivate: Boolean - require authentication (true = protected)
 * 
 * Client Types:
 * - generic: Generic document client (uses GenericDocument, GenericDocumentDetails)
 * - contract (default): Contract-focused client (uses Document, DocumentDetails)
 * 
 * Navigation Categories:
 * 1. Authentication Routes: Login, Logout, LoginSuccess
 * 2. Document Management: Document, DocumentDetails, Clauses
 * 3. Data Exploration: DataExplorer, Portfolio, Combinations
 * 4. Configuration/Admin: TaxonomyManager, AgentConfig, AuditLog
 * 5. AI Features: AskGenie, ContractReview, AgentLearningSummary
 * 6. User Account: Account, Profile, Notifications
 * 
 * Dependencies:
 * - React: UI library
 * - react-router-dom: Routing (used by parent App component)
 * - All imported page components from ../pages/* modules
 * 
 * @module navigation
 * @exports {Array} nav - Navigation configuration array
 * 
 * @example
 * // Usage in routing (typically in App.js)
 * import { nav } from './navigation'
 * const routes = nav.map(route => <Route key={route.path} {...route} />)
 * 
 * @example
 * // Get menu items only (for navigation component)
 * const menuItems = nav.filter(item => item.isMenu)
 * 
 * @example
 * // Check if route is protected
 * const route = nav.find(r => r.path === '/account')
 * if (route.isPrivate && !isLoggedIn) { // redirect to login }
 */

import React from "react";
import { Routes, Route } from "react-router-dom";
// ===== PAGE COMPONENT IMPORTS =====
// Core Pages
import { About } from "../pages/about";
import { Account } from "../pages/account";
import { Login } from "../pages/login";
import { Logout } from "../pages/logout";
import { Private } from "../pages/private";
import { Document } from "../pages/document";
import { DocumentDetails } from "../pages/documentdetails";
import { LoginSuccess } from "../pages/loginsuccess";
import { ReRunAnalysis } from "../pages/rerunanalysis";

// Document Classification & Management
import { Clauses } from "../pages/clauses";
import { Taxonomies } from "../pages/taxonomies";

// Document Types (Generic vs Specific)
import { GenericDocument } from "../pages/genericdocument";
import { GenericDocumentDetails } from "../pages/genericdocumentdetails";

// Data Management & Connectors
import {Connectors} from "../pages/connector"
import {Notification} from "../pages/notification"
import {TableColumn} from "../pages/tablecolumn"
import {AuditLog} from "../pages/auditlog"
import {Dashboard} from "../pages/dashboard"
// import {DashboardV1} from "../pages/dashboardv1"

// Taxonomy Management
import TaxonomyManager from "../pages/taxonomymanager"
import {TaxonomyAuditLogTable}  from "../pages/auditlogtaxonomy";

// AI/Agent Features
import {AskGenie}  from "../pages/contractgenie";
import {Combination} from "../pages/dataexploercomination"
import {Portfolio} from "../pages/dataexploerportfolio"
import {Exception} from "../pages/dataexploerexception"
import {DataExploer,DataExploerDetailView} from "../pages/dataexploer"
import AgentConfidenceSettings from "../pages/agentconfig"
import AgentRoutedExceptions from "../pages/agentroutedexception"
import AgentLearningSummary from "../pages/agentlearningsummary"
import ContractReview from "../pages/agentcontractreview"

// LicenseeGenie new pages
import RoyaltyCalculator from "../pages/royaltycalculator";
import LicenseExpiryTracker from "../pages/licenseexpirytracker";
import TerritoryMapping from "../pages/territorymapping";
import BrandIPPortfolio from "../pages/brandipportfolio";
import RoyaltyReporting from "../pages/royaltyreporting";

/**
 * Get current client type from environment variables
 * 
 * @type {string}
 * @const
 * @description Client type determines which document routes are loaded
 * - "generic" = Generic document management client
 * - Default (undefined) = Contract-focused client
 * 
 * Set via REACT_APP_CLIENT environment variable in .env files
 * @example
 * // In .env.development
 * REACT_APP_CLIENT=generic
 */
const app_client = process.env.REACT_APP_CLIENT;

/**
 * Complete Navigation Configuration Array
 * 
 * Defines all available routes in the application with metadata
 * Structure is conditionally modified based on client type at initialization
 * 
 * Properties for each route object:
 * @property {string} path - URL path (supports React Router params like :id)
 * @property {string} name - Component/page identifier
 * @property {JSX.Element} element - React component to render at this route
 * @property {boolean} isMenu - Show in navigation menu (true = visible, false = hidden)
 * @property {boolean} isPrivate - Require authentication (true = protected, false = public)
 * 
 * @type {Array<Object>}
 * @constant
 */
let nav = [
  // ===== AUTHENTICATION ROUTES =====
  /**
   * Root path redirects to login
   * Public route, not shown in menu
   */
  { path: "/", name: "Login", element: <Login />, isMenu: false, isPrivate: false },
  
  /**
   * About page - public information
   * Visible in menu, accessible without login
   */
  { path: "/about", name: "About", element: <About />, isMenu: true, isPrivate: false },
  
  /**
   * Main login page
   * Entry point for unauthenticated users
   * Not shown in menu (already at login)
   */
  { path: "/login", name: "Login", element: <Login />, isMenu: false, isPrivate: false },
  
  /**
   * Private/test route
   * Requires authentication
   * Hidden from menu
   */
  { path: "/private", name: "Private", element: <Private />, isMenu: true, isPrivate: true },
  
  /**
   * User account/profile settings
   * Requires authentication
   * Visible in user menu
   */
  { path: "/account", name: "Account", element: <Account />, isMenu: true, isPrivate: true },
  
  /**
   * Login success confirmation page
   * Shown after successful authentication
   * Not in main menu
   */
  { path: "/login-success", name: "LoginSuccess", element: <LoginSuccess />, isMenu: false, isPrivate: false },
  
  /**
   * Logout handler route
   * Clears session and redirects to login
   * Visible in user menu dropdown
   */
  { path: "/logout", name: "Logout", element: <Logout />, isMenu: true, isPrivate: false },
  
  // ===== DOCUMENT ANALYSIS & MANAGEMENT =====
  /**
   * Re-run analysis on previously analyzed document
   * Allows user to re-process document with updated settings
   */
  { path: "/re-run-analysis", name: "ReRunAnalysis", element: <ReRunAnalysis />, isMenu: false, isPrivate: false },
  
  /**
   * Manage and view document clauses
   * Extracted clause definitions and templates
   */
  { path: "/clauses", name: "Clauses", element: <Clauses />, isMenu: false, isPrivate: false },
  
  /**
   * Taxonomy management and classification
   * Define and manage document classification categories
   */
  { path: "/taxonomies", name: "Taxonomies", element: <Taxonomies />, isMenu: false, isPrivate: false },
  
  // ===== DATA CONNECTORS & INTEGRATIONS =====
  /**
   * Data source connector configuration
   * Set up and manage connections to external data sources
   */
  { path: "/connector", name: "Connectors", element: <Connectors />, isMenu: false, isPrivate: false },
  
  /**
   * User notifications and alerts
   * View system notifications and important updates
   */
  { path: "/notifications", name: "Notification", element: <Notification />, isMenu: false, isPrivate: false },
  
  /**
   * Table column customization
   * Configure which columns display in data tables
   */
  { path: "/table-edit", name: "TableColumn", element: <TableColumn />, isMenu: false, isPrivate: false },
  
  // ===== AUDIT & LOGGING =====
  /**
   * Audit log viewer
   * Track document changes, user actions, system events
   */
  { path: "/audit-log", name: "AuditLog", element: <AuditLog />, isMenu: false, isPrivate: false },
  
  // ===== DATA EXPLORATION & ANALYTICS =====
  /**
   * Data explorer - combination view (legacy route)
   * Multi-dimensional data analysis
   */
  { path: "/data-explorer-old", name: "Combination", element: <Combination />, isMenu: false, isPrivate: false },
  
  /**
   * Data explorer - combination view (current)
   * Analyze document data in multiple dimensions
   */
  { path: "/data-explorer-comination", name: "Combination", element: <Combination />, isMenu: false, isPrivate: false },
  
  /**
   * Portfolio data explorer
   * Portfolio-level analytics and insights
   */
  { path: "/data-portfolio-explorer", name: "Portfolio", element: <Portfolio />, isMenu: false, isPrivate: false },
  
  /**
   * Main data explorer
   * Query and analyze document data
   */
  { path: "/data-explorer", name: "DataExploer", element: <DataExploer />, isMenu: false, isPrivate: false },
  
  /**
   * Data explorer detail view - by view ID
   * Detailed analysis of specific data views
   * Params: viewid, viewname
   */
  { path: "/data-explorer-details/viewid/:viewid/viewname/:viewname", name: "Combination", element: <Combination />, isMenu: false, isPrivate: false },
  
  /**
   * Data explorer detail view - by type and filter
   * Detailed view of filtered data
   * Params: type (document type), filtervalue (filter criteria)
   */
  { path: "/data-explorer-details/:type/:filtervalue", name: "Combination", element: <Combination />, isMenu: false, isPrivate: false },
  
  /**
   * Exception/anomaly viewer
   * Identify and manage data anomalies and exceptions
   */
  { path: "/data-exception-explorer", name: "Exception", element: <Exception />, isMenu: false, isPrivate: false },
  
  // ===== TAXONOMY & CONFIGURATION =====
  /**
   * Taxonomy management interface
   * Create and maintain document classification taxonomies
   */
  { path: "/taxonomy-management", name: "TaxonomyManagement", element: <TaxonomyManager />, isMenu: false, isPrivate: false },
  
  /**
   * Taxonomy change audit log
   * Track modifications to taxonomy definitions
   */
  { path: "/taxonomy-audit-log", name: "TaxonomyAuditLog", element: <TaxonomyAuditLogTable />, isMenu: false, isPrivate: false },
  
  // ===== AI & AGENT FEATURES =====
  /**
   * AskGenie AI assistant
   * Ask questions about documents and get AI-powered answers
   */
  { path: "/askgenie", name: "AskGenie", element: <AskGenie />, isMenu: false, isPrivate: false },
  
  /**
   * Agent confidence settings configuration
   * Fine-tune AI agent confidence thresholds and parameters
   */
  { path: "/agent-confidence-settings", name: "AgentConfidenceSettings", element: <AgentConfidenceSettings />, isMenu: false, isPrivate: false },
  
  /**
   * Agent routed exceptions management
   * Manage exceptions routed to human review by AI agents
   */
  { path: "/agent-routed-exceptions", name: "AgentRoutedExceptions", element: <AgentRoutedExceptions />, isMenu: false, isPrivate: false },
  
  /**
   * Agent learning summary
   * View AI agent learning progress and improvement metrics
   */
  { path: "/agent-learning-summary", name: "AgentLearningSummary", element: <AgentLearningSummary />, isMenu: false, isPrivate: false },
  
  /**
   * Contract review by AI agent
   * AI-powered contract analysis and review
   * Params: contractId - specific contract to review
   */
  { path: "/contract/:contractId/review", name: "ContractReview", element: <ContractReview />, isMenu: false, isPrivate: false },
];

// Add new LicenseeGenie routes
nav = nav.concat([
  { path: "/royalty-calculator", name: "RoyaltyCalculator", element: <RoyaltyCalculator />, isMenu: true, isPrivate: false },
  { path: "/license-expiry-tracker", name: "LicenseExpiryTracker", element: <LicenseExpiryTracker />, isMenu: true, isPrivate: false },
  { path: "/territory-mapping", name: "TerritoryMapping", element: <TerritoryMapping />, isMenu: true, isPrivate: false },
  { path: "/brand-ip-portfolio", name: "BrandIPPortfolio", element: <BrandIPPortfolio />, isMenu: true, isPrivate: false },
  { path: "/royalty-reporting", name: "RoyaltyReporting", element: <RoyaltyReporting />, isMenu: true, isPrivate: false },

  // LicenseeGenie mockup pages
  { path: "/financial-forecasting", name: "FinancialForecasting", element: <FinancialForecasting />, isMenu: true, isPrivate: false },
  { path: "/licensed-product-catalog", name: "LicensedProductCatalog", element: <LicensedProductCatalog />, isMenu: true, isPrivate: false },
  { path: "/compliance-monitoring", name: "ComplianceMonitoring", element: <ComplianceMonitoring />, isMenu: true, isPrivate: false },
  { path: "/contract-comparison", name: "ContractComparison", element: <ContractComparison />, isMenu: true, isPrivate: false },
  { path: "/audit-trail", name: "AuditTrail", element: <AuditTrail />, isMenu: true, isPrivate: false },
]);

/**
 * CONDITIONAL ROUTING BASED ON CLIENT TYPE
 * 
 * Dynamic route loading determined by REACT_APP_CLIENT environment variable
 * Allows same codebase to serve different client types with different document pages
 * 
 * Generic Client (REACT_APP_CLIENT="generic"):
 * - Uses GenericDocument and GenericDocumentDetails components
 * - Supports more flexible document structure
 * - Routes: /document-list, /document-details/:documentid/:documentname/:type
 * 
 * Contract Client (default, REACT_APP_CLIENT not set or other value):
 * - Uses Document and DocumentDetails components
 * - Optimized for contract document analysis
 * - Routes: /document-list, /document-details/:documentid/:type
 */
if (app_client === "generic") {
  /**
   * Generic client routes - flexible document management
   * 
   * Routes added for generic client:
   * - /document-list: View all documents (shown in menu)
   * - /document-details/:documentid/:documentname/:type: Document details
   */
  nav = nav.concat([
    // Document list page - shows all documents
    // Visible in menu, includes documentid, documentname, and type parameters
    { path: "/document-list", name: "GenericDocument", element: <GenericDocument />, isMenu: true, isPrivate: false },
    
    // Document detail page - shows specific document details
    // Params: documentid (unique doc ID), documentname (doc display name), type (doc type)
    { path: "/document-details/:documentid/:documentname/:type", name: "GenericDocumentDetails", element: <GenericDocumentDetails />, isMenu: true, isPrivate: false },
  ]);
} else {
  /**
   * Contract client routes - contract-focused document management
   * 
   * Routes added for contract client:
   * - /document-list: View all contracts (shown in menu)
   * - /document-details/:documentid/:type: Contract details
   */
  nav = nav.concat([
    // Document list page - shows all contracts
    // Visible in menu for quick access to contracts
    { path: "/document-list", name: "Document", element: <Document />, isMenu: true, isPrivate: false },
    
    // Document detail page - shows specific contract details
    // Params: documentid (contract unique ID), type (contract type/category)
    // Note: Fewer params than generic client (no documentname)
    { path: "/document-details/:documentid/:type", name: "DocumentDetails", element: <DocumentDetails />, isMenu: true, isPrivate: false },
  ]);
}

/**
 * EXPORT NAVIGATION CONFIGURATION
 * 
 * Export the complete navigation array for use in App.js routing
 * The array is already conditioned on client type before export
 * 
 * Usage:
 * - import { nav } from './navigation'
 * - nav.forEach(route => routes.push(<Route {...route} />))
 * 
 * The exported array contains:
 * - All core application routes (23 routes minimum)
 * - 2 client-specific routes (added based on app_client value)
 * - Total: 25 routes in standard configuration
 * 
 * Properties for each route enable:
 * - React Router rendering
 * - Menu visibility control
 * - Access control enforcement (isPrivate)
 * - Named route identification
 */
export { nav };

/**
 * NavigationRouter Component
 * Renders all routes defined in nav using react-router-dom
 */
export function NavigationRouter() {
  return (
    <Routes>
      {nav.map((route, idx) => (
        <Route key={route.path || idx} path={route.path} element={route.element} />
      ))}
    </Routes>
  );
}
